import { AfterViewInit, Component, Input, OnChanges, OnInit, SimpleChange, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TslToastrService, TslCheckboxChange } from '@tesla/ng-tds';
import { AgencyType, DiversitySelection, DiversityType, SupplierDiversity } from 'app/profile/models/onboarding.model';
import { OPERATION, Supplier, SupplierPropertyValue } from 'app/profile/models/supplier.model';
import { GetDiversityTypes, GetAgencyTypes } from 'app/profile/store/app.actions';
import { SelectAgencyType, SelectDiversityTypes } from 'app/profile/store/app.selector';
import { SupplierState } from 'app/profile/store/state/supplierstate.state';
import { CustomValidator } from 'app/shared/custom-validator/custom-validator';
import { IncrementLoaderCount } from 'app/shared/store/actions';
import { Guid, validateAllFormFields } from 'app/shared/utils/utils';
import { combineLatest } from 'rxjs';
import { isNullOrUndefined } from 'util';
import { DiversityDocumentGridComponent } from '../documents/diversity-document-grid/diversity-document-grid.component';
import { I18nService } from 'app/shared/services/i18n.service';

@Component({
  selector: 'diversity-edit',
  templateUrl: './diversity-edit.component.html',
  styleUrls: ['./diversity-edit.component.scss']
})
export class DiversityEditComponent implements OnInit, OnChanges, AfterViewInit {

  @Input('tsl-supplierDiversity') supplierDiversity: SupplierDiversity = new SupplierDiversity();
  @ViewChild(DiversityDocumentGridComponent, { static: true }) documentGrid: DiversityDocumentGridComponent;

  councilCertifications: AgencyType[] = [];
  GovtCertifications: AgencyType[] = [];
  diversityTypes: DiversityType[] = [];

  mainForm: FormGroup;

  requiredDocuments = ["WBE"];
  supplierCode = '188041';

  get controls() {
    return {
      IsDiversityOwned: this.mainForm.get('IsDiversityOwned'),
      OwnershipPercentage: this.mainForm.get('OwnershipPercentage'),
      DiversityTypes: this.mainForm.get('DiversityTypes'),
      IsCouncilCertified: this.mainForm.get('IsCouncilCertified'),
      CouncilCertifications: this.mainForm.get('CouncilCertifications'),
      IsGovtCertified: this.mainForm.get('IsGovtCertified'),
      GovtCertifications: this.mainForm.get('GovtCertifications'),
    }
  }

  // diversitydocs = [];
  // eldocs = [];
  constructor(
    private translate: I18nService,
    private fb: FormBuilder,
    private store: Store<SupplierState>) {
    this.mainForm = fb.group({
      IsDiversityOwned: [null, Validators.required],
      OwnershipPercentage: [null, Validators.required],
      DiversityTypes: this.fb.array([]),
      IsCouncilCertified: [null, Validators.required],
      CouncilCertifications: this.fb.array([]),
      IsGovtCertified: [null, Validators.required],
      GovtCertifications: this.fb.array([]),
      Documents: []
    });
  }

  ngOnInit() {
    this.store.dispatch(new GetDiversityTypes());
    this.store.dispatch(new GetAgencyTypes());
    this.controls.IsDiversityOwned.valueChanges.subscribe(() => this.setDiversityDocumentForm());
    this.controls.IsCouncilCertified.valueChanges.subscribe(() => this.setAgencyDocumentForm());
    this.controls.IsGovtCertified.valueChanges.subscribe(() => this.setGovtCertificationsDocumentForm());

    combineLatest([
      this.store.select(SelectDiversityTypes),
      this.store.select(SelectAgencyType)
    ]).subscribe(([types, agencies]) => {
      this.diversityTypes = types;
      if (agencies) {
        this.councilCertifications = agencies.filter(x => x.Type == 'Council' || x.Type == null);
        this.GovtCertifications = agencies.filter(x => x.Type == 'Government' || x.Type == null);
      }
      this.setFormValue(this.supplierDiversity);
    })


  }

  setFormValue(supplierDiversity: SupplierDiversity) {
    this.supplierDiversity = supplierDiversity;
    if (this.supplierDiversity) {
      this.mainForm.patchValue({
        IsDiversityOwned: this.supplierDiversity.IsDiversityOwned,
        OwnershipPercentage: this.supplierDiversity.OwnershipPercentage,
        IsCouncilCertified: this.supplierDiversity.IsCouncilCertified,
        IsGovtCertified: this.supplierDiversity.IsGovtCertified,
        Documents: this.supplierDiversity.Documents
      });
    }
  }

  ngAfterViewInit() {

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.supplierDiversity) {
      this.setFormValue(this.supplierDiversity);
    }
  }

  /** Sets the diversity docuemnts form */
  private setDiversityDocumentForm() {
    if (this.controls.IsDiversityOwned && this.controls.IsDiversityOwned.value == 'Yes') {
      this.controls.OwnershipPercentage.setValidators(Validators.required);
      this.mainForm.setControl('DiversityTypes', this.fb.array(
        this.diversityTypes.map(d => {
          const grp = this.fb.group({
            selected: false,
            data: d,
            percentage: null,
            comment: null,
          });

          if (this.supplierDiversity && this.supplierDiversity.DiversityTypes && this.supplierDiversity.DiversityTypes) {
            const existingData = this.supplierDiversity.DiversityTypes.find(divType => d.Code == divType.code);
            if (existingData) {

              grp.patchValue({
                selected: existingData != null,
                percentage: existingData.percentage,
                comment: existingData.comment
              })
            }
          }
          return grp;
        }), CustomValidator.atLeastOneFormArrayValidator
      ));
    } else {
      this.mainForm.setControl('DiversityTypes', this.fb.array([]));
      this.controls.OwnershipPercentage.clearValidators();
    }
  }

  private setAgencyDocumentForm() {
    if (this.controls.IsCouncilCertified && this.controls.IsCouncilCertified.value == 'Yes') {
      // Create CouncilCertifications
      this.mainForm.setControl('CouncilCertifications', this.fb.array(
        this.councilCertifications.map(d => {
          const grp = this.fb.group({
            selected: false,
            data: d,
            document: null,
            comment: null,
          });

          // Prepopulate saved data
          if (this.supplierDiversity && this.supplierDiversity.CouncilCertifications && this.supplierDiversity.CouncilCertifications) {
            const existingData = this.supplierDiversity.CouncilCertifications.find(divType => d.Code == divType.code);
            if (existingData) {
              var documentType = this.councilCertifications.find(x => x.Code == existingData.code).DocumentType;
              this.updateDocumentGrid(documentType);
              grp.patchValue({
                selected: existingData != null,
                data: d,
                document: existingData.document,
                comment: existingData.comment,
              });
            }
          }
          return grp;
        }
        ), CustomValidator.atLeastOneFormArrayValidator
      ));
    } else {
      this.mainForm.setControl('CouncilCertifications', this.fb.array([]))
    }
  }

  private setGovtCertificationsDocumentForm() {
    if (this.controls.IsGovtCertified && this.controls.IsGovtCertified.value == 'Yes') {
      // Create GovtCertifications
      this.mainForm.setControl('GovtCertifications', this.fb.array(
        this.GovtCertifications.map(d => {
          const grp = this.fb.group({
            selected: false,
            data: d,
            document: null,
            comment: null,
          });

          // Prepopulate saved data
          if (this.supplierDiversity && this.supplierDiversity.GovtCertifications && this.supplierDiversity.GovtCertifications) {
            const existingData = this.supplierDiversity.GovtCertifications.find(divType => d.Code == divType.code);
            if (existingData) {
              var documentType = this.GovtCertifications.find(x => x.Code == existingData.code).DocumentType;
              this.updateDocumentGrid(documentType);
              grp.patchValue({
                selected: existingData != null,
                data: d,
                document: existingData.document,
                comment: existingData.comment,
              });
            }
          }
          return grp;
        }
        ), CustomValidator.atLeastOneFormArrayValidator
      ));

    } else {
      this.mainForm.setControl('GovtCertifications', this.fb.array([]))
    }
  }

  updateDocumentGrid(documentType: string) {
    var document = this.supplierDiversity?.Documents?.find(x => x.DocumentType == documentType);
    if (document)
      this.documentGrid.upsertDocumentType({ DocumentType: documentType, Attachments: [document], IsRequired: true, EffectiveDate: document.EffectiveDate, ExpiryDate: document.ExpiryDate });
    else
      this.documentGrid.upsertDocumentType({ DocumentType: documentType, Attachments: null, IsRequired: true, EffectiveDate: null, ExpiryDate: null });

  }

  onCertificationChange(event: TslCheckboxChange) {
    if (event.checked) {
      this.documentGrid.upsertDocumentType({ DocumentType: event.source.name, Attachments: [], IsRequired: true });
    }
    else {
      this.documentGrid.removeDocumentType(event.source.name);
    }
  }

  getEventPayload() {
    const supplier = new Supplier();
    const payload =  this.getPayload();

    if (payload.IsCouncilCertified == 'Yes' || payload.IsGovtCertified == 'Yes') {
      supplier.Documents = payload.Documents;
      if (!payload.DocumentIds) {
        payload.DocumentIds = [];
      }

      supplier.Documents.forEach(doc => {
        if (isNullOrUndefined(doc.StageID)) {
          doc.StageID = Guid.newGuid();
          doc.Operation = OPERATION.CREATE;
          payload.DocumentIds.push(doc.StageID);
        }
      })
    }

    supplier.Diversity = payload;
    return supplier;
  }

  getPayload(){
    const payload = new SupplierDiversity();
    payload.IsDiversityOwned = this.controls.IsDiversityOwned.value;
    payload.IsCouncilCertified = this.controls.IsCouncilCertified.value;
    payload.IsGovtCertified = this.controls.IsGovtCertified.value;
    if (payload.IsDiversityOwned == 'Yes') {
      payload.OwnershipPercentage = this.controls.OwnershipPercentage.value;
      payload.DiversityTypes = this.controls.DiversityTypes.value.filter(x => x.selected == true)
        .map(x => {
          x.code = x.data.Code;
          delete x.data;
          delete x.selected;
          return x
        });
    }
    if (payload.IsCouncilCertified == 'Yes') {
      payload.CouncilCertifications = this.controls.CouncilCertifications.value.filter(x => x.selected == true)
        .map(x => {
          x.code = x.data.Code;
          delete x.data;
          delete x.selected;
          return x
        });
    }
    if (payload.IsGovtCertified == 'Yes') {
      payload.GovtCertifications = this.controls.GovtCertifications.value.filter(x => x.selected == true).map(x => {
        x.code = x.data.Code;
        delete x.data;
        delete x.selected;
        return x
      });
    }
    if (payload.IsCouncilCertified == 'Yes' || payload.IsGovtCertified == 'Yes') {
      payload.Documents = this.documentGrid.Value;
      if (!payload.DocumentIds) {
        payload.DocumentIds = [];
      }
    }

    return payload;
  }

  isFieldInvalid(formGroup: FormGroup, field: string): boolean {
    if (formGroup) {
      return !formGroup.get(field).valid && formGroup.get(field).touched;
    } else {
      return false;
    }
  }
}
